import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'sec-1', label: 'Our Role: Controller vs. Processor' },
  { id: 'sec-2', label: 'Information We Collect' },
  { id: 'sec-3', label: 'How We Use Your Information' },
  { id: 'sms-terms', label: 'SMS/Text Communications' },
  { id: 'sec-5', label: 'Data Sharing and Storage' },
  { id: 'sec-6', label: 'Data Security' },
  { id: 'sec-7', label: 'Waiver Verification Data' },
  { id: 'sec-8', label: 'Your Rights' },
  { id: 'sec-9', label: 'Cookies & Tracking Technologies' },
  { id: 'beacon', label: 'BookingFlows Beacon & Browser Extension' },
  { id: 'sec-10', label: 'GDPR (European Users)' },
  { id: 'sec-11', label: 'CCPA (California Residents)' },
  { id: 'sec-12', label: 'UAE Users (PDPL)' },
  { id: 'sec-13', label: 'Data Retention' },
  { id: 'sec-14', label: 'Contact Us' },
];

const PrivacyPolicy = () => (
  <LegalPageLayout title="Privacy Policy" lastUpdated="July 11, 2026" toc={TOC}>
    <LegalSection id="sec-1" number={1} title="Our Role: Controller vs. Processor">
      <p>BookingFlows acts in two distinct capacities, and responsibility for personal data follows the role:</p>
      <ul>
        <li><strong>For Operator account data</strong> (the businesses that sign up to use BookingFlows), we act as the <strong>data controller</strong> — we decide how account, billing, and platform-usage data is processed.</li>
        <li><strong>For Customer booking data</strong> (end users who book through an Operator&apos;s booking page or widget), the <strong>Operator is the data controller</strong> and BookingFlows acts as a <strong>data processor</strong>, processing that data solely on the Operator&apos;s behalf and instructions to power bookings, tickets, waivers, and notifications. Customers should direct privacy requests regarding their booking data to the Operator they booked with; we will assist Operators in fulfilling such requests.</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-2" number={2} title="Information We Collect">
      <p>
        We collect information you provide directly to us when you create an account, use our services, or communicate with us. This includes:
      </p>
      <ul>
        <li>Account information (name, email, phone number, organization details)</li>
        <li>Booking information (dates, times, services, customer details)</li>
        <li>Payment information (processed securely through Stripe)</li>
        <li>Device information (IP address, browser type, device fingerprints for waiver verification)</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-3" number={3} title="How We Use Your Information">
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, maintain, and improve our booking platform</li>
        <li>Process bookings and payments</li>
        <li>Send booking confirmations, reminders, and updates</li>
        <li>Provide customer support</li>
        <li>Verify digital waiver signatures with device fingerprinting</li>
        <li>Prevent fraud and ensure platform security</li>
      </ul>
    </LegalSection>

    <LegalSection id="sms-terms" number={4} title="SMS/Text Message Communications">
      <p>
        BookingFlows and its platform operators may send SMS/text messages to users for transactional and informational purposes. By providing your phone number during booking, account creation, or checkout, you consent to receive text messages related to your bookings and account.
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Types of Messages</h3>
      <p>You may receive the following types of SMS messages:</p>
      <ul>
        <li>Booking confirmations and receipts</li>
        <li>Reservation reminders and schedule updates</li>
        <li>One-time passwords (OTP) for account verification</li>
        <li>Two-factor authentication (2FA) codes</li>
        <li>Ticket or receipt delivery links</li>
        <li>Account notifications and security alerts</li>
      </ul>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Consent and Opt-In</h3>
      <p>
        By entering your phone number and completing a booking or creating an account, you expressly consent to receive transactional SMS messages from BookingFlows and the experience operator you booked with. <strong>SMS is not used for promotional or marketing content.</strong>
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Message Frequency</h3>
      <p>
        Message frequency varies based on your bookings and account activity. Typically, you will receive 1-5 messages per booking (confirmation, reminder, follow-up).
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Opt-Out Instructions</h3>
      <LegalCallout>
        <strong>To stop receiving SMS messages:</strong> Reply <strong>STOP</strong> to any message. You will receive a confirmation and no further messages will be sent. You may also contact us at support@bookingflows.com to opt out.
      </LegalCallout>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Help</h3>
      <p>
        For help with SMS messages, reply <strong>HELP</strong> to any message or contact us at support@bookingflows.com.
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Message and Data Rates</h3>
      <LegalCallout>
        <strong>Message and data rates may apply.</strong> Standard messaging rates from your wireless carrier may apply to messages sent and received. Check with your carrier for details about your plan.
      </LegalCallout>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Carriers Supported</h3>
      <p>
        SMS messages are supported on all major U.S. carriers including AT&amp;T, Verizon, T-Mobile, Sprint, and others. Carrier delivery is not guaranteed.
      </p>
    </LegalSection>

    <LegalSection id="sec-5" number={5} title="Data Sharing and Storage">
      <p>
        <strong>Your data is yours.</strong> We do not sell your customer data to third parties. Each organization gets its own dedicated database. We only share data with:
      </p>
      <ul>
        <li>Service providers (Stripe for payments, Twilio for SMS, email providers)</li>
        <li>As required by law or legal process</li>
        <li>With your explicit consent</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-6" number={6} title="Data Security">
      <p>We use industry-standard security measures to protect your data, including:</p>
      <ul>
        <li>Encryption of data at rest and in transit</li>
        <li>Secure authentication with JWT tokens</li>
        <li>Regular security audits and monitoring</li>
        <li>Isolated databases for each organization</li>
        <li>Daily encrypted, tamper-protected backups stored on Amazon Web Services (AWS)</li>
        <li>Primary data hosted in the European Union (Frankfurt, Germany)</li>
      </ul>
      <p>
        No method of transmission or storage is 100% secure. While we use commercially reasonable safeguards, we cannot guarantee absolute security, and Operators are responsible for safeguarding their own account credentials and managing team member access appropriately.
      </p>
    </LegalSection>

    <LegalSection id="sec-7" number={7} title="Waiver Verification Data">
      <p>
        When customers sign digital waivers, we collect device fingerprints, IP addresses, browser information, and signature data. This information is stored securely and used exclusively for legal verification purposes. Access to this sensitive data is logged in our activity log.
      </p>
    </LegalSection>

    <LegalSection id="sec-8" number={8} title="Your Rights">
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Correct inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data</li>
        <li>Opt-out of marketing communications</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-9" number={9} title="Cookies and Tracking Technologies">
      <p>
        We use cookies and similar tracking technologies to improve your experience, understand usage patterns, and deliver relevant content. Here&apos;s what you need to know:
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Types of Cookies We Use</h3>
      <ul>
        <li><strong>Essential Cookies:</strong> Required for basic site functionality like authentication, security, and session management. These cannot be disabled.</li>
        <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website (Google Analytics). These are only loaded with your consent.</li>
        <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track campaign effectiveness. These are only loaded with your consent.</li>
      </ul>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Managing Your Cookie Preferences</h3>
      <p>When you first visit our site, you&apos;ll see a cookie consent banner where you can:</p>
      <ul>
        <li>Accept all cookies</li>
        <li>Decline non-essential cookies</li>
        <li>Customize your preferences by category</li>
      </ul>
      <p>
        You can change your preferences at any time by clearing your browser&apos;s localStorage and refreshing the page, or through your browser&apos;s cookie settings.
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Third-Party Services</h3>
      <p>
        We may use third-party analytics and advertising services that place their own cookies. These services have their own privacy policies:
      </p>
      <ul>
        <li>Google Analytics — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
        <li>Stripe (payments) — <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
      </ul>
    </LegalSection>

    <LegalSection id="beacon" number={10} title="BookingFlows Beacon & Beacon Tender Browser Extension">
      <h3 className="text-base font-semibold text-gray-800 pt-2">The BookingFlows Beacon</h3>
      <p>
        The BookingFlows Beacon is a lightweight analytics script (<code>beacon.js</code>) that Operators may install on
        their own websites to measure their website traffic and how it converts into bookings. The Operator installing the
        Beacon is the data controller for their website analytics; BookingFlows processes this data on the Operator&apos;s behalf.
      </p>
      <p>For each anonymous page view or booking-widget step, the Beacon collects only:</p>
      <ul>
        <li>Page URL (origin and path only — query strings are not stored), referrer, and UTM campaign tags when present</li>
        <li>Device class derived from window width only (&quot;mobile&quot;, &quot;tablet&quot;, or &quot;desktop&quot;)</li>
        <li>Randomly generated anonymous visitor and session identifiers, stored first-party in the visited website&apos;s own browser storage</li>
        <li>Event type (e.g. page view, widget opened, checkout started, booking completed)</li>
      </ul>
      <LegalCallout>
        The Beacon collects <strong>no personally identifiable information</strong> — no names, emails, phone numbers, form
        inputs, keystrokes, or page content. No IP addresses are stored with analytics events, no device fingerprinting is
        performed, no third-party cookies are set, and visitors are never tracked across unrelated websites.
      </LegalCallout>
      <p>
        When a visitor clicks from an Operator&apos;s website to that same Operator&apos;s booking page, the Beacon may pass its
        anonymous visitor ID along (e.g. <code>?bf_vid=…</code>) solely so the Operator can see that a booking originated
        from their website. Beacon data is used exclusively to provide the installing Operator with aggregate analytics
        about their own website. Removing the Beacon script from a website stops all collection immediately, and clearing
        browser site data removes the anonymous identifiers.
      </p>
      <h3 className="text-base font-semibold text-gray-800 pt-2">Beacon Tender Browser Extension</h3>
      <p>
        BookingFlows Beacon Tender is a browser extension with a single purpose: verifying that the Beacon is correctly
        installed on a web page. It runs <strong>only when the user clicks the extension icon</strong> (via the browser&apos;s
        activeTab permission), inspects the current page&apos;s HTML solely to detect the presence of a <code>beacon.js</code> script
        tag, and — if found — sends only that tag&apos;s Beacon ID (a site configuration value placed in the page by the site
        owner, not user data) to a BookingFlows endpoint to confirm events are being received.
      </p>
      <LegalCallout>
        The extension collects <strong>no user data whatsoever</strong>: no personal information, no browsing history, no
        page content beyond detecting the Beacon tag, and no analytics about the extension user. It never runs in the
        background, stores nothing, and uninstalling it leaves nothing behind.
      </LegalCallout>
      <p>
        We do not sell or transfer Beacon or extension data to third parties; we do not use or transfer it for purposes
        unrelated to providing website analytics to the installing Operator; and we do not use or transfer it to determine
        creditworthiness or for lending purposes.
      </p>
    </LegalSection>

    <LegalSection id="sec-10" number={11} title="GDPR Compliance (European Users)">
      <p>
        If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
      </p>
      <ul>
        <li><strong>Right to Access:</strong> Request a copy of all personal data we hold about you</li>
        <li><strong>Right to Rectification:</strong> Request correction of inaccurate personal data</li>
        <li><strong>Right to Erasure:</strong> Request deletion of your personal data (&quot;right to be forgotten&quot;)</li>
        <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
        <li><strong>Right to Data Portability:</strong> Request your data in a machine-readable format</li>
        <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
        <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
      </ul>
      <p>
        <strong>Legal Basis for Processing:</strong> We process your data based on: (a) your consent, (b) performance of a contract, (c) compliance with legal obligations, or (d) our legitimate business interests.
      </p>
    </LegalSection>

    <LegalSection id="sec-11" number={12} title="CCPA Compliance (California Residents)">
      <p>
        If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights:
      </p>
      <ul>
        <li><strong>Right to Know:</strong> You can request information about what personal data we collect, use, and disclose</li>
        <li><strong>Right to Delete:</strong> You can request deletion of your personal data, subject to certain exceptions</li>
        <li><strong>Right to Opt-Out:</strong> You can opt-out of the sale of your personal information</li>
        <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
      </ul>
      <LegalCallout>
        <strong>We Do Not Sell Your Personal Information.</strong> BookingFlows does not sell, rent, or trade your personal information to third parties for monetary or other valuable consideration.
      </LegalCallout>
      <p>
        To exercise your CCPA rights, please contact us at the email below. We will respond to verifiable requests within 45 days.
      </p>
    </LegalSection>

    <LegalSection id="sec-12" number={13} title="UAE Users (PDPL)">
      <p>
        If you are located in the United Arab Emirates, the UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021, &quot;PDPL&quot;) grants you rights similar to the GDPR, including the right to access, correct, and request deletion of your personal data, the right to restrict or object to processing, and the right to data portability. We process UAE personal data on the legal bases of contract performance, legal obligation, legitimate interest, or consent. Where an Operator in the UAE controls your booking data, that Operator is responsible for PDPL compliance as controller, and we support them as processor. To exercise your rights, contact us at the email below.
      </p>
    </LegalSection>

    <LegalSection id="sec-13" number={14} title="Data Retention">
      <p>
        We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including:
      </p>
      <ul>
        <li>Active account data: Retained while your account is active</li>
        <li>Booking records: Retained for 7 years for legal and tax compliance</li>
        <li>Waiver signatures: Retained for the statute of limitations period in your jurisdiction</li>
        <li>Analytics data: Anonymized after 26 months</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-14" number={15} title="Contact Us">
      <p>If you have questions about this Privacy Policy, please contact us at:</p>
      <p className="font-semibold text-gray-800">support@bookingflows.com</p>
    </LegalSection>
  </LegalPageLayout>
);

export default PrivacyPolicy;
